import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DataGrid, GridColumnProps } from '@roc/ui';
import { observer } from 'mobx-react';

const useStyles = makeStyles(() => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '65vh',
    overflow: 'hidden',
  },
}));

interface LoansInPendingStatusGridProps {
  gridStore: any;
  columns: GridColumnProps[];
}

export const LoansInPendingStatusGrid = observer(
  forwardRef(({ gridStore, columns }: LoansInPendingStatusGridProps, ref) => {
    const classes = useStyles();
    const gridApiRef = useRef<any>(null);

    const LOCALHOST_PORT = 'localhost:3000';

    const frameworkComponents = {
      currencyCellRenderer: ({ value }) =>
        value != null
          ? `$${Number(value).toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}`
          : '',
      rateCellRenderer: ({ value }) => (value != null ? `${value}%` : ''),
      loanIdLinkRenderer: ({ value }) => {
        const parentOrigin = window.location.ancestorOrigins[0];
        const isLocalhost = parentOrigin.includes(LOCALHOST_PORT);
        const basePath = isLocalhost
          ? `${parentOrigin}/#/loan_information/${value}`
          : `${parentOrigin}/nportal#/loan_information/${value}`;

        return (
          <span
            style={{
              color: '#1a73e8',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            onClick={() => {
              window.open(basePath, '_blank');
            }}
          >
            {value}
          </span>
        );
      },
    };

    const onGridReady = params => {
      gridApiRef.current = params.api;
    };

    useImperativeHandle(ref, () => ({
      refresh: () => gridApiRef.current?.refreshCells?.({ force: true }),
    }));

    const rows = gridStore?.gridData?.data?.rows || [];

    return (
      <Box>
        <Grid item className={classes.gridContainer}>
          <DataGrid
            columns={columns}
            rows={rows}
            frameworkComponents={frameworkComponents}
            isLoading={gridStore?.isLoading}
            showFilters={gridStore?.showFilters}
            sortModel={gridStore?.sortModel}
            filterModel={gridStore?.filterModel}
            paginationData={gridStore?.gridData?.meta}
            setPageNumber={gridStore?.setPageNumber}
            gridReady={onGridReady}
            onFilterChanged={params =>
              gridStore.setFilterModel?.(params.api.getFilterModel())
            }
            onSortChanged={params =>
              gridStore.setSortModel?.(params.api.getSortModel())
            }
          />
        </Grid>
      </Box>
    );
  })
);
